import { useEffect } from 'react';
import { assetConstants } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import { type UseQueryResult, useQueries } from '@tanstack/react-query';
import { getPendingDeposits } from '@/shared/graphql/cache/channels';
import { cacheClient } from '@/shared/graphql/client';
import { type GetPendingDepositsQuery } from '@/shared/graphql/generated/cache/graphql';
import useChainTracking from './useChainTracking';
import useStore, { type StoredDepositChannel } from './useStore';

const combine = (
  results: UseQueryResult<
    { queryResult: GetPendingDepositsQuery; channel: StoredDepositChannel },
    Error
  >[],
) =>
  new Map(
    results
      .map((result) => {
        if (!result.data) return null;

        const { channel, queryResult } = result.data;

        return [
          channel.asset,
          queryResult.pendingDeposits.filter(isNotNullish).map((d) => ({
            amount: d.amount,
            depositAddress: channel.depositAddress,
            channelId: channel.channelId,
            chain: assetConstants[channel.asset].chain,
            issuedBlockId: channel.blockNumber,
            txRef: d.txRef,
          })),
        ] as const;
      })
      .filter(isNotNullish),
  );

export default function usePendingDeposits() {
  const channels = useStore((state) => state.channels);
  const removeChannel = useStore((state) => state.removeChannel);
  const chainTracking = useChainTracking({ enabled: channels.length > 0 });

  useEffect(() => {
    if (chainTracking == null) return;
    for (const channel of channels) {
      const { chain } = assetConstants[channel.asset];
      const height = chainTracking[chain];
      const expiryblock = channel.depositChainExpiryBlock;
      if (expiryblock < height) {
        removeChannel(channel.depositAddress);
      }
    }
  }, [chainTracking, channels]);

  return useQueries({
    queries: channels.map(
      (channel) =>
        ({
          refetchInterval: 5_000,
          queryKey: ['PENDING_DEPOSITS', channel.depositAddress],
          queryFn: async () =>
            cacheClient.request(getPendingDeposits, {
              address: channel.depositAddress as string,
              asset: channel.asset,
              chain: assetConstants[channel.asset].chain,
            }),
          select: (queryResult: GetPendingDepositsQuery) => ({ queryResult, channel }),
        }) as const,
    ),
    combine,
  });
}
